import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";

const STATUSES = [["paid", "Paid"], ["approved", "Approved"], ["pending", "Pending"], ["rejected", "Rejected"], ["all", "All"]];
const BADGE = { paid: "bg-green-100 text-green-700", approved: "bg-blue-100 text-blue-700", pending: "bg-amber-100 text-amber-700", rejected: "bg-red-100 text-red-700" };
const yearOf = (e) => (e.paid_at || e.approved_at || e.created_at || "").slice(0, 4);

function Capsules({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${value === k ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 hover:border-slate-400"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function GeneralExpensesList() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("paid");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  useEffect(() => { authAxios.get("/expenses/general").then(r => setItems(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const years = useMemo(() => {
    const ys = [...new Set(items.map(yearOf).filter(Boolean))].sort().reverse();
    if (!ys.includes(year)) ys.unshift(year);
    return [...ys.map(y => [y, y]), ["all", "All years"]];
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = items.filter(e => (status === "all" || e.status === status) && (year === "all" || yearOf(e) === year));
  const total = shown.filter(e => ["approved", "paid"].includes(e.status) && !e.is_capex).reduce((a, e) => a + (e.amount || 0), 0);

  return (
    <div className="mt-4 pt-4 border-t space-y-2" data-testid="general-expenses">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-[#1e3a5f]">General expense claims</p>
        <p className="text-sm font-bold text-red-500" data-testid="general-expenses-total" title="Counted in costs">-€{total.toFixed(2)}</p>
      </div>
      <Capsules options={years} value={year} onChange={setYear} testid="general-expenses-year" />
      <Capsules options={STATUSES} value={status} onChange={setStatus} testid="general-expenses-status" />
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {shown.map(e => (
          <div key={e.id} className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm bg-slate-50" data-testid={`general-expense-${e.id}`}>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${BADGE[e.status] || "bg-slate-100 text-slate-500"}`}>{e.status}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{e.submitter_name} – {e.description}</p>
              <p className="text-xs text-slate-400 truncate">{e.category || "Uncategorised"} | {(e.paid_at || e.approved_at || e.created_at || "").slice(0, 10)}{e.is_capex ? " | CAPEX" : ""}</p>
            </div>
            <span className={`font-bold shrink-0 ${["pending", "rejected"].includes(e.status) ? "text-slate-400" : "text-red-500"}`}>-€{(e.amount || 0).toFixed(2)}</span>
          </div>
        ))}
        {!shown.length && <p className="text-xs text-slate-400 text-center py-3" data-testid="general-expenses-empty">No general expenses for this selection</p>}
      </div>
      <p className="text-[11px] text-slate-400">Approved and paid claims (excluding CAPEX) count towards total costs; pending and rejected are shown for reference.</p>
    </div>
  );
}
